import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import IconButton from "@material-ui/core/IconButton";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import PauseIcon from "@material-ui/icons/Pause";
import Tooltip from "@material-ui/core/Tooltip";
import { useDispatch } from "react-redux";
import { setAnalyticsDetails } from "../../../features/layers/overlaylayerslice";
import { ToastContainer, toast } from 'react-toastify';

const TimelineHourly = ({ data, fulldates }) => {
  const dispatch = useDispatch();
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [hoveredStep, setHoveredStep] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const sliderRef = useRef(null);
  const intervalTime = 3000;

  // Function to add 1 day to each date
  const adjustDateRange = (dates) => {
    return dates.map(date => {
      const [year, month, day] = date.split('-').map(Number);
      const newDate = new Date(year, month - 1, day + 1); // Add 1 day
      const newYear = newDate.getFullYear();
      const newMonth = String(newDate.getMonth() + 1).padStart(2, '0');
      const newDay = String(newDate.getDate()).padStart(2, '0');
      return `${newYear}-${newMonth}-${newDay}`;
    });
  };

  const adjustedDates = adjustDateRange(fulldates);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const togglePlayPause = () => {
    setIsPlaying((prevState) => !prevState);
  };

  useEffect(() => {
    let interval;
    if (isPlaying && currentStep < adjustedDates.length - 1) {
      interval = setInterval(() => {
        setCurrentStep((prevStep) => {
          const nextStep = prevStep + 1;
          if (nextStep >= adjustedDates.length) {
            setIsPlaying(false);
            return prevStep;
          }
          dispatch(
            setAnalyticsDetails({
              id: data.id - 1,
              date: adjustedDates[nextStep]+'-hr-00-00-00',
              isPlaying: true,
            })
          );
          return nextStep;
        });
      }, intervalTime);
    } else {
      clearInterval(interval);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentStep, adjustedDates, dispatch, data.id]);

  // useEffect(()=>{
  //   dispatch(
  //           setAnalyticsDetails({
  //             id: data.id - 1,
  //             date: adjustedDates[0]+'-hr-00-00-00',
  //             isPlaying: true,
  //           })
  //         );
  // },[adjustedDates])
  const handleStepChange = (e) => {
    if (!sliderRef.current) return;
    
    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.min(Math.max(x / rect.width, 0), 1);
    const newStep = Math.round(percent * (adjustedDates.length - 1));
    
    setCurrentStep(newStep);
    setIsPlaying(false);
    dispatch(setAnalyticsDetails({ 
      id: data.id - 1, 
      date: adjustedDates[newStep]+'-hr-00-00-00',
      isPlaying: true, 
    }));
  };

  const handleMouseMove = (e) => {
    if (!sliderRef.current) return;
    
    const slider = sliderRef.current;
    const rect = slider.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = Math.min(Math.max(x / rect.width, 0), 1);
    const step = Math.round(percent * (adjustedDates.length - 1));
    
    setHoveredStep(step);
  };

  const shouldShowLabel = (index) => {
    if (index === 0 || index === adjustedDates.length - 1) return true;
    if (adjustedDates.length <= 5) return true;
    if (isMobile) {
      const step = Math.floor(adjustedDates.length / 3);
      return index % step === 0;
    }
    const step = Math.floor(adjustedDates.length / 6);
    return index % step === 0;
  };

  const formatDate = (dateString) => {
    if (isMobile) {
      const [year, month, day] = dateString.split('-');
      return `${month}/${day}`;
    }
    return dateString;
  };

  return (
    <TimelineWrapper>
      <ControlsContainer>
        <PlayPauseButton $isMobile={isMobile} onClick={togglePlayPause}>
          {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
        </PlayPauseButton>
        
        <SliderContainer $isMobile={isMobile}>
          <SliderTrack 
            ref={sliderRef}
            onClick={handleStepChange}
            onMouseMove={handleMouseMove}
            onMouseLeave={() => setHoveredStep(null)}
            $isMobile={isMobile}
          >
            <SliderProgress style={{ width: `${(currentStep / (adjustedDates.length - 1)) * 100}%` }} />
            
            {!isMobile && (
              <Tooltip
                open={true}
                title={`Date: ${adjustedDates[currentStep]}`}
                placement="top"
                arrow
              >
                <SliderThumb 
                  style={{ left: `${(currentStep / (adjustedDates.length - 1)) * 100}%` }} 
                  $isMobile={isMobile}
                />
              </Tooltip>
            )}
            
            {adjustedDates.map((date, index) => (
              <React.Fragment key={index}>
                {(!isMobile || index % 5 === 0) && (
                  <SliderMark 
                    style={{ left: `${(index / (adjustedDates.length - 1)) * 100}%` }}
                    onMouseEnter={() => setHoveredStep(index)}
                    $isMobile={isMobile}
                  />
                )}
                {shouldShowLabel(index) && (
                  <SliderLabel 
                    style={{ left: `${(index / (adjustedDates.length - 1)) * 100}%` }}
                    $isMobile={isMobile}
                  >
                    {formatDate(date)}
                  </SliderLabel>
                )}
              </React.Fragment>
            ))}
            
            {!isMobile && hoveredStep !== null && hoveredStep !== currentStep && (
              <Tooltip
                open={true}
                title={`Date: ${adjustedDates[hoveredStep]}`}
                placement="top"
                arrow
              >
                <HoverIndicator style={{ left: `${(hoveredStep / (adjustedDates.length - 1)) * 100}%` }} />
              </Tooltip>
            )}
          </SliderTrack>
          
          {isMobile && (
            <MobileDateDisplay>
              {adjustedDates[currentStep]}
            </MobileDateDisplay>
          )}
        </SliderContainer>
      </ControlsContainer>
      
      <ToastContainer position="bottom-left" autoClose={3000} />
    </TimelineWrapper>
  );
};

const TimelineWrapper = styled.div`
  display: flex;
  align-items: center;
  padding: 8px 10px;
  color: white;
  border-radius: 8px;
  position: fixed;
  z-index: 999;
  bottom: 30px;
  left: 10px;
  right: 10px;
  background: rgba(3, 57, 105, 0.66);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  max-width: calc(100% - 20px);
  box-sizing: border-box;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

  @media (min-width: 480px) {
    padding: 10px 15px;
    bottom: 15px;
  }

  @media (min-width: 768px) {
    left: 20px;
    right: 20px;
    bottom: 20px;
    max-width: calc(100% - 40px);
    padding: 12px 20px;
  }
`;

const ControlsContainer = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  gap: 12px;

  @media (min-width: 480px) {
    gap: 20px;
  }

  @media (min-width: 768px) {
    gap: 25px;
  }
`;

const PlayPauseButton = styled(IconButton)`
  color: #ff5722;
  width: ${props => props.$isMobile ? '32px' : '40px'};
  height: ${props => props.$isMobile ? '32px' : '40px'};
  background-color: white !important;
  border-radius: 50%;
  flex-shrink: 0;
  padding: 0;
  
  &:hover {
    transform: scale(1.1);
  }

  & svg {
    font-size: ${props => props.$isMobile ? '20px' : '24px'};
  }
`;

const SliderContainer = styled.div`
  flex-grow: 1;
  min-width: 0;
  padding: ${props => props.$isMobile ? '5px 0' : '10px 0'};
  position: relative;
`;

const SliderTrack = styled.div`
  position: relative;
  width: 100%;
  height: ${props => props.$isMobile ? '3px' : '4px'};
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  cursor: pointer;
`;

const SliderProgress = styled.div`
  position: absolute;
  height: 100%;
  background: #ff5722;
  border-radius: 2px;
`;

const SliderThumb = styled.div`
  position: absolute;
  width: ${props => props.$isMobile ? '12px' : '14px'};
  height: ${props => props.$isMobile ? '12px' : '14px'};
  background: #ff5722;
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 2;
`;

const SliderMark = styled.div`
  position: absolute;
  width: ${props => props.$isMobile ? '4px' : '6px'};
  height: ${props => props.$isMobile ? '4px' : '6px'};
  background: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  cursor: pointer;
  z-index: 1;
`;

const SliderLabel = styled.div`
  position: absolute;
  top: ${props => props.$isMobile ? '8px' : '12px'};
  transform: translateX(-50%);
  font-size: ${props => props.$isMobile ? '9px' : '10px'};
  color: white;
  white-space: nowrap;
  font-weight: bold;
  z-index: 1;

  @media (min-width: 480px) {
    font-size: ${props => props.$isMobile ? '10px' : '11px'};
    top: ${props => props.$isMobile ? '10px' : '15px'};
  }
`;

const HoverIndicator = styled.div`
  position: absolute;
  width: 10px;
  height: 10px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 1;
`;

const MobileDateDisplay = styled.div`
  margin-top: 8px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  text-align: center;
  width: 100%;
`;

export default TimelineHourly;